import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpParams,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import * as Sentry from '@sentry/angular';

import { IAuthAccessToken, ELocalStorageKeys } from '@account/data-access';
import { AuthStore } from '@auth/data-access';
import { APP_CONFIG, ApiUrlsType } from '@application/config';
import {
  createSentryEventThrottle,
  ErrorStore,
} from '@application/errors/data-access';
import { LocalStorageFacade } from '@application/localstorage';

import { OFFLINE_CONFIRM_MS } from './network-status';
import { IS_TOKEN_REFRESH_RETRY } from './token.interceptor';

import { DateTime } from 'luxon';

/**
 * Explicit `service` tags, keyed by a host fragment shared by the REST and OData
 * endpoints of that service across every environment (dev/stg/prd). The
 * OData/REST split is only a frontend concern, so both share one tag.
 *
 * This table no longer decides *what* is captured — {@link resolveServiceTag}
 * does, from the environment's own host list — only what it is *named*. It holds
 * the six services that were monitored while it was the allowlist, so their tags
 * keep the exact spelling their Sentry issues, saved searches and alerts already
 * use: the tag is part of the fingerprint, so renaming one splits every issue it
 * has into two. Note `goodservice-api` → `goods_service`, which the derivation
 * below could not have guessed.
 *
 * A new service needs no entry; add one only to override the derived tag.
 */
const SERVICE_TAG_OVERRIDES: ReadonlyArray<{
  hostFragment: string;
  tag: string;
}> = [
  { hostFragment: 'subscriptionservice-api', tag: 'subscription_service' },
  { hostFragment: 'receivableservice-api', tag: 'receivable_service' },
  { hostFragment: 'workoutservice-api', tag: 'workout_service' },
  {
    hostFragment: 'physicalassessmentservice-api',
    tag: 'physical_assessment_service',
  },
  { hostFragment: 'goodservice-api', tag: 'goods_service' },
  { hostFragment: 'workservice-api', tag: 'work_service' },
];

/**
 * Hosts that are ours, and only those: every value of the running environment's
 * `apiUrl` table, which is the one place that already knows every backend this
 * app talks to — `academyservice-api` and `channelgateway-api` included, and
 * `pague.la`, the map tiles and the CDNs excluded.
 *
 * Reading it beats listing services by hand: a backend used to be invisible in
 * Sentry until someone remembered to register it, which is how the academy
 * service failed in production reporting nothing. It also beats reporting
 * *every* request: the app's own static files (`assets/i18n/pt-BR.json`) resolve
 * against an origin that is never in `apiUrl`, so they stay out.
 *
 * Loopback is dropped on purpose. `acessoLocalhost` is the access agent
 * installed on the reception's own machine; it is *expected* to be absent on
 * every other station, and reporting it would bury the backend failures under
 * a fault that is not one.
 */
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

/**
 * Cached by the identity of the `apiUrl` object rather than by a boolean: the
 * table never changes within a page, but it does change between the tests that
 * exercise this file, and a plain `let built = true` would leak the first
 * environment into all of them.
 */
let cachedApiUrl: ApiUrlsType | null = null;
let cachedMonitoredHosts: ReadonlySet<string> = new Set<string>();

function monitoredHosts(apiUrl: ApiUrlsType): ReadonlySet<string> {
  if (apiUrl === cachedApiUrl) return cachedMonitoredHosts;

  const hosts = new Set<string>();
  for (const value of Object.values(apiUrl)) {
    if (typeof value !== 'string') continue;
    try {
      const { hostname } = new URL(value);
      if (hostname && !LOOPBACK_HOSTS.has(hostname)) {
        hosts.add(hostname.toLowerCase());
      }
    } catch {
      // Not an absolute URL — nothing to monitor.
    }
  }

  cachedApiUrl = apiUrl;
  cachedMonitoredHosts = hosts;
  return hosts;
}

/**
 * Builds a `service` tag from a host when no override matches, so a backend is
 * named the day it is first called instead of the day someone registers it. It
 * uses the DNS label that carries the `-api` suffix — the part that names the
 * service — and drops the environment and domain that follow, so one tag covers
 * dev, stg and prd: `academyservice-api.prd.o.actuar.cloud` → `academy_service`.
 *
 * Looking for the `-api` label rather than taking the first one is what keeps
 * `grpc.accessedgemanager-api…` from being tagged `grpc`. A label ending in
 * `service` reads better split (`personservice` → `person_service`), matching how
 * the pre-existing tags were spelled by hand; anything else keeps its label with
 * dashes normalized (`authentication-api` → `authentication`).
 */
function deriveServiceTag(hostname: string): string {
  const labels = hostname.split('.');
  const label = labels.find((part) => part.endsWith('-api')) ?? labels[0];

  return label
    .replace(/-api$/, '')
    .replace(/service$/, '_service')
    .replace(/-/g, '_');
}

/**
 * Headers omitted from the generated curl. They are dropped entirely (not
 * printed as `<redacted>`) so the curl string never contains keywords like
 * "authorization", which would make Sentry's server-side data scrubber replace
 * the whole `curl` field with `[Filtered]`.
 */
const OMITTED_HEADERS = [
  'authorization',
  'cookie',
  'set-cookie',
  'x-xsrf-token',
  // Still *sent* — only kept out of the curl. Its value carries
  // `sentry-public_key=…`, and a `key=` in the string is what makes Sentry's
  // own scrubber replace the whole `curl` field with `[Filtered]`, taking the
  // request with it. `traceparent` and `sentry-trace` carry the same ids and
  // stay, so a replayed curl still reproduces the trace.
  'baggage',
];

/**
 * The `service` tag for a URL, or `null` when the host is not one of ours and
 * the request is therefore not reported at all.
 */
function resolveServiceTag(
  url: string,
  hosts: ReadonlySet<string>
): string | null {
  let hostname: string;
  try {
    // A relative URL has no host of its own; resolving it against a base that is
    // never in `apiUrl` is what keeps `assets/…` out without a second rule.
    // `https` e não `http` só para não puxar o portão de segurança do Sonar para
    // baixo: o esquema é irrelevante num endereço-sentinela que existe para dar
    // base a uma URL relativa, e `relative.invalid` nunca está em `apiUrl`.
    hostname = new URL(url, 'https://relative.invalid').hostname.toLowerCase();
  } catch {
    return null;
  }

  if (!hosts.has(hostname)) return null;

  const override = SERVICE_TAG_OVERRIDES.find((entry) =>
    hostname.includes(entry.hostFragment)
  );
  return override ? override.tag : deriveServiceTag(hostname);
}

/**
 * Trace identifiers sent *with the request*, so a Sentry issue and a backend log
 * line can be matched by hand.
 *
 * The direction is the whole point. A `status === 0` — the failure this file
 * exists to explain — delivers no response to JavaScript at all: not a body, not
 * a header. Whatever the backend might have said about itself on the way back is
 * unreachable by construction, so the only identifier that can survive the trip
 * is one we put on the way out.
 *
 * `getTraceData` needs no `tracesSampleRate`: with no active span it falls back
 * to the scope's propagation context, which is what the SDK calls tracing
 * without performance. That context is created once per page load and holds only
 * a trace id, so `traceId` is the same for every request of a session while the
 * span id is generated fresh on each call — the trace id finds the session in
 * the backend log, the span id finds the request.
 *
 * It returns `{}` when the SDK is disabled, which is every environment but
 * production. Nothing here is observable outside prod.
 */
const TRACE_HEADER_NAMES = ['sentry-trace', 'baggage', 'traceparent'] as const;

/** `00-<32 hex trace id>-<16 hex span id>-<2 hex flags>`, the W3C shape. */
const TRACEPARENT_PATTERN = /^00-([0-9a-f]{32})-([0-9a-f]{16})-[0-9a-f]{2}$/i;

function traceHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  try {
    const data = Sentry.getTraceData({ propagateTraceparent: true }) as Record<
      string,
      string | undefined
    >;
    for (const name of TRACE_HEADER_NAMES) {
      const value = data[name];
      if (value) headers[name] = value;
    }
  } catch {
    // Reporting must never be what breaks the request it is reporting on.
  }

  return headers;
}

/**
 * Field names whose values never reach Sentry. Matched as a substring of the
 * key, case-insensitively, so `Password`, `novaSenha`, `refresh_token` and
 * `X-Api-Key` are all caught.
 *
 * The six-service allowlist kept the authentication, user and account services
 * out of Sentry by accident, and with them the credentials they carry. Now that
 * every backend in `apiUrl` is reported, a failed login would otherwise
 * serialize its password straight into the `curl` under "Additional Data".
 * Sentry's server-side scrubber is not a substitute: it does not see inside a
 * `curl` string we build ourselves.
 *
 * The card vocabulary is here because the list was written for a login form and
 * only ever knew the English words: the Pague.la card endpoint names its fields
 * `numero`, `codigoSeguranca`, `dataValidade` and `nomeComprador`, so a failed
 * card registration serialized a live card number and its security code into a
 * production issue. `numero` is anchored on purpose — as a substring it would
 * also eat `numeroParcelas`, `NumeroContrato` and `numeroDocumento`, and a curl
 * with its identifiers blanked out is no longer worth capturing.
 *
 * A name list only ever protects the vocabulary whoever wrote it imagined, so
 * it is not the whole defence: see {@link looksLikeCardNumber}.
 */
const SENSITIVE_CREDENTIAL_KEY =
  /password|senha|secret|token|credential|authorization|apikey/;

const SENSITIVE_CARD_KEY =
  /cvv|cvc|securitycode|cardnumber|numerocartao|^numero$|codigoseguranca|datavalidade|nomecomprador|portador|titular/;

/**
 * A Web Push subscription (`/web-push/subscriptions` of the access directory):
 * the endpoint identifies the person's browser and, with the `p256dh` and
 * `auth` keys, is what a push is encrypted to. All anchored — `endpoint` as a
 * substring would eat `endpointUrl`-style config fields, and `auth` would eat
 * `author` and `authorizedBy`.
 */
const SENSITIVE_PUSH_SUBSCRIPTION_KEY = /^endpoint$|^p256dh$|^auth$/;

/**
 * Whether a field name is one whose value never reaches Sentry.
 *
 * The key is normalized once — lower-cased, with dashes and underscores dropped
 * — so neither list has to spell every separator a backend might pick. That is
 * what lets `codigoSeguranca`, `codigo_seguranca` and `CODIGO-SEGURANCA` be one
 * entry instead of three, and it keeps each pattern simple enough to read.
 *
 * Two lists rather than one because they are two different things that changed
 * for two different reasons: the credential list was written for a login form,
 * the card list was written the day a card registration serialized a live PAN
 * into a production issue. Keeping them apart is what stops the next incident
 * from being appended to a regex nobody can read any more.
 */
function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase().replaceAll(/[-_]/g, '');
  return (
    SENSITIVE_CREDENTIAL_KEY.test(normalized) ||
    SENSITIVE_CARD_KEY.test(normalized) ||
    SENSITIVE_PUSH_SUBSCRIPTION_KEY.test(normalized)
  );
}

const REDACTED = '[redacted]';

/** Guard against a body deeper than anything worth sending, or cyclic. */
const MAX_REDACT_DEPTH = 8;

/**
 * Length window of a payment card number (ISO/IEC 7812). Outside it a Luhn hit
 * is far likelier to be an ordinary identifier than a card.
 */
const MIN_CARD_DIGITS = 13;
const MAX_CARD_DIGITS = 19;

/**
 * Whether a leaf value *is* a payment card number: digits only once spaces and
 * dashes are dropped, a plausible length, and a valid Luhn check digit.
 *
 * This is the half of the redaction that survives a field name nobody
 * predicted, and it is the half that matters. {@link isSensitiveKey} has to be
 * taught every new spelling one incident at a time; the next gateway will name
 * its fields something else again, and the value is the same either way.
 *
 * A leaf is matched whole, never as a substring, so a boleto line or a document
 * number quoted inside a longer sentence does not trigger it. A phone number
 * that happens to satisfy Luhn is a false positive we accept: losing one field
 * of a curl costs a little diagnosability, and printing a card number costs a
 * compliance incident.
 */
function looksLikeCardNumber(value: unknown): boolean {
  if (typeof value !== 'string' && typeof value !== 'number') return false;

  const digits = String(value).replace(/[\s-]/g, '');
  if (!/^\d+$/.test(digits)) return false;
  if (digits.length < MIN_CARD_DIGITS || digits.length > MAX_CARD_DIGITS) {
    return false;
  }

  // Luhn: walking from the right, every second digit is doubled — and a
  // doubling that passes 9 has 9 subtracted, which is the same as adding its
  // two digits. A card number is one whose total is a multiple of ten.
  let sum = 0;
  let doubling = false;
  for (let index = digits.length - 1; index >= 0; index--) {
    // `charCodeAt` rather than a numeric cast: the repository bans the ad-hoc
    // number parsers, and this is a digit that is already known to be one.
    let digit = digits.charCodeAt(index) - 48;
    if (doubling) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    doubling = !doubling;
  }

  return sum % 10 === 0;
}

/**
 * A copy of `value` with every sensitive field replaced by {@link REDACTED}.
 * Copies rather than mutates: the object handed in is the live request body,
 * still owned by the caller that may be about to retry with it.
 */
function redactSensitive(value: unknown, depth = 0): unknown {
  if (depth > MAX_REDACT_DEPTH || value == null) return value;

  if (Array.isArray(value)) {
    return value.map((item) => redactSensitive(item, depth + 1));
  }

  if (typeof value !== 'object') {
    return looksLikeCardNumber(value) ? REDACTED : value;
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, item]) => [
      key,
      isSensitiveKey(key) ? REDACTED : redactSensitive(item, depth + 1),
    ])
  );
}

/**
 * The same redaction for an `a=1&b=2` string — the query of a URL, or the whole
 * body of a form-encoded request, which is where a login sends its password.
 *
 * Split by hand rather than through `URLSearchParams`: that would re-encode
 * every untouched parameter, and the OData `$filter` of a failed grid call is
 * most of what makes the issue readable.
 */
function redactQueryString(query: string): string {
  return query
    .split('&')
    .map((pair) => {
      const separator = pair.indexOf('=');
      if (separator === -1) return pair;

      const key = pair.slice(0, separator);
      if (!isSensitiveKey(key) && !looksLikeCardNumber(pair.slice(separator + 1))) {
        return pair;
      }
      return `${key}=${REDACTED}`;
    })
    .join('&');
}

/**
 * Applied to both the `curl` and the URL on the issue: some endpoints carry a
 * token as a query parameter, and the URL is reported whether or not the
 * request had a body.
 */
function redactUrl(url: string): string {
  const queryStart = url.indexOf('?');
  if (queryStart === -1) return url;

  return `${url.slice(0, queryStart)}?${redactQueryString(
    url.slice(queryStart + 1)
  )}`;
}

/**
 * Redacts a body that arrived already serialized. Anything that is not JSON is
 * returned untouched: there is no structure to find a field name in.
 */
function redactJsonString(body: string): string {
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed == null || typeof parsed !== 'object') return body;
    return JSON.stringify(redactSensitive(parsed));
  } catch {
    return body;
  }
}

function serializeBody(body: unknown): string | null {
  if (body == null) return null;
  if (body instanceof HttpParams) {
    return redactQueryString(body.toString());
  }
  if (
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof ArrayBuffer
  ) {
    return '[binary body omitted]';
  }
  // A string body is opaque — unless it is JSON, which is what a payload built
  // by hand instead of by `HttpClient` looks like.
  if (typeof body === 'string') return redactJsonString(body);
  try {
    return JSON.stringify(redactSensitive(body));
  } catch {
    return String(body);
  }
}

/**
 * Reconstructs a copy-pasteable `curl` command for the failed request so the
 * call can be replayed from a terminal. Sensitive headers are omitted entirely.
 */
/**
 * Escapes a value for use inside a single-quoted shell string: each `'` becomes
 * `'\''` (close quote, escaped quote, reopen quote).
 */
function shellEscape(value: string): string {
  return value.replace(/'/g, `'\\''`);
}

function buildCurl(request: HttpRequest<unknown>): string {
  const parts = [
    `curl -X ${request.method} '${redactUrl(request.urlWithParams)}'`,
  ];

  for (const name of request.headers.keys()) {
    // Dropped, not redacted: leaving the *name* of a credential header in the
    // string is enough for Sentry's scrubber to replace the whole `curl` field
    // with `[Filtered]`, taking the diagnosable part of the request with it.
    if (
      OMITTED_HEADERS.includes(name.toLowerCase()) ||
      isSensitiveKey(name)
    )
      continue;
    const value = request.headers.getAll(name)?.join(', ') ?? '';
    parts.push(`-H '${name}: ${shellEscape(value)}'`);
  }

  const body = serializeBody(request.body);
  if (body != null) {
    parts.push(`--data '${shellEscape(body)}'`);
  }

  parts.push('--compressed');
  return parts.join(' \\\n  ');
}

/**
 * Normalizes the HTTP error response body for "Additional Data". The backend
 * sometimes returns a parsed object and sometimes a plain string (e.g. a raw
 * text/HTML error page), so both shapes are handled. Objects are pretty-printed
 * to a JSON string so the *entire* nested structure is visible on the issue:
 * passing the raw object to `setExtra` lets Sentry's normalizer truncate deep
 * levels (default depth 3), rendering nested values as `[Array]`/`[Object]`.
 * Strings are returned verbatim; binary payloads are summarized.
 */
function serializeResponse(body: unknown): unknown {
  if (body == null) return null;
  // A `status === 0` hands us the XHR's own `ProgressEvent`, not a response.
  // Serializing it writes `{"isTrusted": true}` into the one field on the issue
  // where the backend's reason is meant to be, which reads as "the backend
  // answered something useless" when the truth is that it never answered.
  if (typeof Event !== 'undefined' && body instanceof Event) return null;
  if (typeof body === 'string') return body;
  if (body instanceof Blob || body instanceof ArrayBuffer) {
    return '[binary response omitted]';
  }
  try {
    return JSON.stringify(redactSensitive(body), null, 2);
  } catch {
    return body;
  }
}

/** Canonical GUID, the id shape most of our backends use. */
const GUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Opaque identifiers that are neither a GUID nor a number — the edge serials of
 * `accessedgemanager` (`vedODY8VNHuVee`), and any other backend that hands out a
 * short random token as a key. Left in the path they split one failure into an
 * issue per device: the 409 on `POST /v1/edge-command/{serial}/release/1` was
 * arriving as a new Sentry issue for every catraca in the fleet.
 *
 * Deliberately narrow, because a false positive is worse than a miss — it merges
 * two genuinely different endpoints into one issue. A segment is only an id when
 * it is at least 8 characters of *plain* alphanumerics carrying both a digit and
 * a letter:
 *  - the digit is what keeps route names out. Ours read `edge-command`,
 *    `get-biometric-readers`, `override-denied`, `ReceivableSettings` — words,
 *    with no digit in them.
 *  - excluding `-` and `_` is the same guard from the other side: every route
 *    name here is kebab-case, so a hyphenated segment with a digit in it
 *    (`oauth2-token`) is a route, not a key.
 *
 * An id that happens to be all letters or shorter than 8 characters is missed
 * and keeps splitting its issue; widening the rule to catch it must not start
 * matching the route names above.
 */
const OPAQUE_ID_SEGMENT =
  /^(?=[A-Za-z0-9]*\d)(?=[A-Za-z0-9]*[A-Za-z])[A-Za-z0-9]{8,}$/;

function isIdentifierSegment(segment: string): boolean {
  return (
    /^\d+$/.test(segment) ||
    GUID_SEGMENT.test(segment) ||
    OPAQUE_ID_SEGMENT.test(segment)
  );
}

/**
 * Response headers the browser lets JavaScript read, for the "response" context.
 *
 * Cross-origin that is the seven CORS-safelisted names plus whatever the backend
 * chooses to name in `Access-Control-Expose-Headers` — today, none of ours name
 * anything, so this is empty until one of them starts publishing its request id.
 * The context is written only when there is something in it, so the issue gains
 * the section the day that happens and carries no empty box until then.
 *
 * A `status === 0` never has any: the response was withheld from JavaScript
 * whole, headers included. That is why the ids also travel outbound — see
 * {@link traceHeaders}.
 */
function readableResponseHeaders(
  error: HttpErrorResponse
): Record<string, string> | null {
  const headers: Record<string, string> = {};

  for (const name of error.headers.keys()) {
    if (OMITTED_HEADERS.includes(name.toLowerCase()) || isSensitiveKey(name)) {
      continue;
    }
    const value = error.headers.get(name);
    if (value) headers[name] = value;
  }

  return Object.keys(headers).length ? headers : null;
}

/**
 * How long a `status === 0` survived before it died, bucketed.
 *
 * Two failures that share the `response_blocked` classification can be nothing
 * alike. A POST to the card gateway that died in 24 ms was turned away at the
 * door — nothing waited for anything. An `enroll-fingerprint` that died after
 * 15 s waited on something upstream and was cut off. Same tag, same title, same
 * issue, and two different teams to talk to; so the bucket joins the fingerprint
 * and the title, the way the classification itself already does.
 */
const INSTANT_FAILURE_MS = 1_000;
const TIMEOUT_FAILURE_MS = 10_000;

type TFailureSpeed = 'instant' | 'slow' | 'timeout';

function classifyFailureSpeed(elapsedMs: number): TFailureSpeed {
  if (elapsedMs < INSTANT_FAILURE_MS) return 'instant';
  if (elapsedMs < TIMEOUT_FAILURE_MS) return 'slow';
  return 'timeout';
}

/**
 * Endpoint path used for grouping, with dynamic segments (GUIDs, numeric ids,
 * opaque keys — see {@link OPAQUE_ID_SEGMENT}) and the query string stripped, so
 * requests to the same endpoint share an issue while different endpoints stay
 * apart.
 *
 * Matching is per segment, not by a substring sweep of the whole path: a numeric
 * *prefix* of a route name is part of the name, and rewriting it (`/2fa` →
 * `/:idfa`) would tell two endpoints apart by a string neither of them has.
 */
function fingerprintPath(url: string): string {
  try {
    const urlObj = new URL(url, 'http://localhost');
    return urlObj.pathname
      .split('/')
      .map((segment) => (isIdentifierSegment(segment) ? ':id' : segment))
      .join('/');
  } catch {
    return url;
  }
}

/**
 * The endpoint as it is named in the issue *title*: the same normalization the
 * fingerprint uses, with the origin kept so the title still says which backend
 * and which environment failed.
 *
 * The title has to be normalized together with the fingerprint, or the grouping
 * fix is invisible: Sentry titles an issue from whichever event opened it, so
 * one arbitrary edge serial would end up naming the failure of the whole fleet.
 * The real URL is not lost — it stays in the `request` context and in the curl,
 * per event.
 */
function fingerprintUrl(url: string): string {
  try {
    const urlObj = new URL(url, 'http://relative.invalid');
    const path = fingerprintPath(url);
    return urlObj.origin === 'http://relative.invalid'
      ? path
      : `${urlObj.origin}${path}`;
  } catch {
    return url;
  }
}

/**
 * Reads, whose offline failures are not reported — the same methods
 * {@link OfflineRetryInterceptor} replays. A read that died with no network
 * says nothing about the backend and, once the connection is back, is fetched
 * again on its own; reporting it buries the real failures under the noise of
 * every lift, tunnel and hotel Wi-Fi.
 *
 * Writes are the exception and stay reported. They are never replayed — a POST
 * that died with `status === 0` may have reached the server — so losing
 * connectivity midway through one is exactly what leaves the backend half-way
 * through an operation (see `PendingMutationInterceptor`), and how often that
 * happens is worth knowing.
 */
const READ_METHODS = new Set(['GET', 'HEAD']);

/**
 * Grace window, after the document starts unloading, during which a `status === 0`
 * failure counts as a navigation abort. Those aborts fire within milliseconds of
 * the teardown, so the window is generous; it exists as an upper bound because
 * `beforeunload` also fires for navigations that never complete (an external
 * handler cancelling the prompt, a `mailto:`/custom-scheme link). A plain boolean
 * would silence `status === 0` reporting for the rest of the session in those
 * cases.
 */
const UNLOAD_GRACE_MS = 5_000;

/** Timestamp of the last unload signal; `0` when the page is not unloading. */
let unloadStartedAt = 0;
let unloadListenersRegistered = false;

/**
 * Starts tracking document teardown so requests killed by it can be told apart
 * from real network failures. `pagehide` is the dependable signal (it also covers
 * the bfcache and, unlike `unload`, does not disable it); `beforeunload` fires
 * earlier and catches aborts that land before `pagehide`. `pageshow` clears the
 * mark because a page restored from the bfcache is alive again and must report
 * normally. Registered once, on the first monitored request.
 */
function trackPageUnload(): void {
  if (unloadListenersRegistered || typeof window === 'undefined') return;
  unloadListenersRegistered = true;

  const markUnloading = () => {
    unloadStartedAt = DateTime.now().toMillis();
  };
  window.addEventListener('beforeunload', markUnloading);
  window.addEventListener('pagehide', markUnloading);
  window.addEventListener('pageshow', () => {
    unloadStartedAt = 0;
  });
}

function isPageUnloading(): boolean {
  return (
    unloadStartedAt !== 0 &&
    DateTime.now().toMillis() - unloadStartedAt < UNLOAD_GRACE_MS
  );
}

/**
 * Classification of a `status === 0` failure.
 *
 * The browser never tells JavaScript *why* a request died: the "blocked by CORS
 * policy" line exists only in the console, emitted by the browser itself, out of
 * reach of any handler. So these are inferences from the little the platform
 * does expose, and they are named after the evidence rather than after a cause
 * we cannot observe.
 *
 * - `offline`: the device had no network. `navigator.onLine === false` is only
 *   trustworthy in this direction — `true` says nothing (captive portals, a
 *   dropped VPN and hotel Wi-Fi all report `true`), so it never *rules out* a
 *   connectivity problem, it only confirms one.
 * - `response_blocked`: the browser has a Resource Timing entry for the request,
 *   meaning an exchange with the server completed — yet nothing reached the app.
 *   That is the shape of a response withheld from JS: an error page from an edge
 *   or gateway that does not echo the CORS headers (a 502/504 answering a
 *   cross-origin call) is the leading suspect. `blocked_status`, when the
 *   browser fills it in, is the actual HTTP status behind the block.
 * - `no_response`: no Resource Timing entry — the request never got as far as a
 *   response. DNS failure, refused connection, or a request killed outright by
 *   an extension or corporate proxy.
 * - `unknown`: Resource Timing is unavailable, so there is nothing to go on.
 */
type TNetworkFailure =
  | 'offline'
  | 'response_blocked'
  | 'no_response'
  | 'unknown';

/** Resource Timing exposes the real HTTP status on some browsers; typings lag. */
type TResourceTiming = PerformanceResourceTiming & { responseStatus?: number };

/** Effective connection info, when the browser exposes the Network Information API. */
type TConnection = {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
};

type TNetworkDiagnostics = {
  reason: TNetworkFailure;
  /** Whether it died on the spot or hung first — see {@link classifyFailureSpeed}. */
  speed: TFailureSpeed;
  /** HTTP status behind a blocked response, when the browser reveals it. */
  blockedStatus: number | null;
  /** How long the browser worked on the request, per Resource Timing. */
  timingDurationMs: number | null;
  online: boolean | null;
  visibility: string | null;
  connection: TConnection | null;
};

/**
 * Newest monitored Resource Timing entries, kept by a `PerformanceObserver`
 * instead of read straight from `performance.getEntriesByName`, because that
 * buffer holds only 250 entries and a screen full of OData calls overruns it in
 * seconds. An overrun would drop the entry of the very request that failed and
 * turn a `response_blocked` into a `no_response` — a wrong classification is
 * worse than none.
 *
 * What keeps the ring small is {@link TRACKED_INITIATORS}, not the set of
 * reported services: now that every backend of the environment is reported,
 * filtering by service would admit a screen's worth of OData calls anyway, and
 * filtering by nothing would admit every image, font, stylesheet and chunk the
 * page loads — the overrun this ring exists to avoid. Only an XHR/fetch entry
 * can belong to a request that reached this interceptor.
 */
const TRACKED_INITIATORS = new Set(['xmlhttprequest', 'fetch']);
const RECENT_TIMINGS_LIMIT = 120;
const recentResourceTimings: TResourceTiming[] = [];
let resourceObserverRegistered = false;

function trackResourceTimings(): void {
  if (
    resourceObserverRegistered ||
    typeof PerformanceObserver === 'undefined'
  ) {
    return;
  }
  resourceObserverRegistered = true;

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as TResourceTiming[]) {
        if (!TRACKED_INITIATORS.has(entry.initiatorType)) continue;
        recentResourceTimings.push(entry);
        if (recentResourceTimings.length > RECENT_TIMINGS_LIMIT) {
          recentResourceTimings.shift();
        }
      }
    });
    // `buffered` replays entries recorded before this observer existed.
    observer.observe({ type: 'resource', buffered: true });
  } catch {
    // No Resource Timing observation available; the lookup falls back to the
    // performance buffer and, failing that, classification degrades to
    // `unknown` rather than guessing.
  }
}

/**
 * Tolerance between the moment the interceptor starts the request and the
 * `startTime` the browser records for it. The entry is normally created after
 * our mark (the XHR is opened downstream), so this only absorbs the opposite
 * ordering under clock granularity.
 */
const TIMING_MATCH_TOLERANCE_MS = 50;

/**
 * Newest Resource Timing entry for this URL that belongs to *this* request.
 *
 * The window check is what makes the classification safe on a retry: a POST that
 * failed and was fired again shares its URL with the previous attempt, and
 * matching by name alone would hand the failed call the successful attempt's
 * entry — reporting `response_blocked` (with the earlier status) for a request
 * that never reached the server.
 */
function findResourceTiming(
  url: string,
  startedAt: number
): TResourceTiming | null {
  const floor = startedAt - TIMING_MATCH_TOLERANCE_MS;

  for (let i = recentResourceTimings.length - 1; i >= 0; i--) {
    const entry = recentResourceTimings[i];
    if (entry.name === url && entry.startTime >= floor) return entry;
  }

  const buffered = performance.getEntriesByName(
    url,
    'resource'
  ) as TResourceTiming[];
  for (let i = buffered.length - 1; i >= 0; i--) {
    if (buffered[i].startTime >= floor) return buffered[i];
  }

  return null;
}

function readConnection(): TConnection | null {
  const connection = (navigator as Navigator & { connection?: TConnection })
    .connection;
  if (!connection) return null;
  return {
    effectiveType: connection.effectiveType,
    downlink: connection.downlink,
    rtt: connection.rtt,
    saveData: connection.saveData,
  };
}

/** Everything the platform will say about a `status === 0` failure. */
function classifyNetworkFailure(
  url: string,
  startedAt: number,
  elapsedMs: number
): TNetworkDiagnostics {
  const online = typeof navigator === 'undefined' ? null : navigator.onLine;
  const visibility =
    typeof document === 'undefined' ? null : document.visibilityState;
  const connection = typeof navigator === 'undefined' ? null : readConnection();

  if (typeof performance === 'undefined') {
    return {
      reason: online === false ? 'offline' : 'unknown',
      speed: classifyFailureSpeed(elapsedMs),
      blockedStatus: null,
      timingDurationMs: null,
      online,
      visibility,
      connection,
    };
  }

  const entry = findResourceTiming(url, startedAt);

  return {
    // Offline outranks the timing evidence: with no network there is nothing
    // for an edge to have answered, whatever the buffer happens to hold.
    reason:
      online === false ? 'offline' : entry ? 'response_blocked' : 'no_response',
    speed: classifyFailureSpeed(elapsedMs),
    blockedStatus: entry?.responseStatus || null,
    timingDurationMs: entry ? Math.round(entry.duration) : null,
    online,
    visibility,
    connection,
  };
}

/**
 * `status === 0` means no HTTP response ever reached the browser: the request was
 * aborted (navigation/teardown), the client lost connectivity, or the real
 * response was blocked by CORS (e.g. a gateway error with no CORS headers).
 *
 * Those caused by the page itself going away — a reload, a tab close, a
 * navigation to another document — are dropped: they say nothing about the
 * backend, they fire in bulk (every in-flight request at once) on every unload,
 * and the report usually cannot even be delivered because the page is already
 * being torn down. A **read** that died with no network is dropped too — see
 * {@link READ_METHODS}. Every other `status === 0` is still reported, since it
 * can hide a real failure (a CORS-masked gateway error). Those that are
 * reported carry a `network_failure` tag saying as much as the browser allows
 * about the cause — see {@link classifyNetworkFailure}.
 *
 * Note this only concerns document-level navigation. Requests cancelled by an
 * RxJS unsubscribe (`switchMap`, `takeUntilDestroyed`, a destroyed component,
 * in-app router navigation) never emit an error at all, so they never reach this
 * interceptor.
 *
 * A 401 is expected during normal operation: when the access token expires, the
 * `TokenInterceptor` refreshes it and replays the request. Reporting that first
 * 401 would flood Sentry with benign token-expiry noise, so we only report a 401
 * that persists *after* the refresh — the retried request carries the
 * {@link IS_TOKEN_REFRESH_RETRY} context marker. Every other status is reported
 * on the first occurrence.
 */
function shouldReport(
  error: HttpErrorResponse,
  request: HttpRequest<unknown>
): boolean {
  if (error.status === 0 && isPageUnloading()) return false;
  if (error.status !== 401) return true;
  return request.context.get(IS_TOKEN_REFRESH_RETRY);
}

/**
 * Identity of whoever hit the error. `PersonId` is only unique *within* a tenant
 * — the same person can be linked to several tenants — so neither id identifies a
 * user on its own: the pair does, and it is what `user.id` carries. Both parts are
 * also exposed as tags so issues can be filtered by tenant alone (e.g. every error
 * from one account) or by person alone.
 */
type SentryUser = { id: string; personId: string; tenantId: string };

function resolveUser(auth: IAuthAccessToken | null): SentryUser | null {
  if (!auth?.PersonId || !auth?.TenantId) {
    return null;
  }

  return {
    id: `${auth.TenantId}:${auth.PersonId}`,
    personId: auth.PersonId,
    tenantId: auth.TenantId,
  };
}

/**
 * Sampling budget. Widening the capture from six services to every backend of
 * the environment widens the *kinds* of failure that arrive, and a backend that
 * goes down is what turns that into volume: one screen retrying a dead endpoint
 * reports the same issue as fast as the user can click, from every open tab.
 *
 * The cooldown is per endpoint failure, not random — see
 * {@link createSentryEventThrottle} for why that is the shape of the limit. The
 * first occurrence of every distinct signature is always kept; only the repeats
 * inside the window are dropped, and the session cap bounds a screen that loops
 * through many distinct ones.
 *
 * Same numbers as the runtime-error path (`sentry-error-reporter.ts`), so the
 * two budgets stay explainable as one rule.
 */
const SIGNATURE_COOLDOWN_MS = 5 * 60 * 1000;
const MAX_EVENTS_PER_SESSION = 50;

const httpErrorThrottle = createSentryEventThrottle({
  cooldownMs: SIGNATURE_COOLDOWN_MS,
  maxPerSession: MAX_EVENTS_PER_SESSION,
});

/**
 * What counts as "the same failure" for the budget: the same parts the
 * fingerprint groups by, minus the backend message. Two validation errors on
 * one endpoint are separate issues but one recurring failure, and it is the
 * recurrence — not the issue count — that the budget is protecting.
 */
function eventSignature(
  service: string,
  request: HttpRequest<unknown>,
  error: HttpErrorResponse,
  diagnostics: TNetworkDiagnostics | null
): string {
  return [
    service,
    request.method,
    fingerprintPath(request.url),
    error.status,
    diagnostics?.reason ?? '',
    diagnostics?.speed ?? '',
  ].join('|');
}

function reportToSentry(
  request: HttpRequest<unknown>,
  error: HttpErrorResponse,
  service: string,
  errorMessage: string | null,
  user: SentryUser | null,
  startedAt: number,
  elapsedMs: number
): void {
  const statusCode = String(error.status);
  const curl = buildCurl(request);

  // Only a `status === 0` needs explaining: every other status already says what
  // happened. See {@link TNetworkFailure} for what each classification means.
  const diagnostics =
    error.status === 0
      ? classifyNetworkFailure(request.urlWithParams, startedAt, elapsedMs)
      : null;

  // A read that failed because the device had no network is not a defect: it is
  // held and replayed by `OfflineRetryInterceptor`, and the user sees the
  // offline modal meanwhile. See {@link READ_METHODS} for why a write is not
  // dropped with it.
  if (
    diagnostics?.reason === 'offline' &&
    READ_METHODS.has(request.method.toUpperCase())
  ) {
    return;
  }

  // Last of the filters, on purpose: a failure dropped above must not spend a
  // slot of the budget, nor start a cooldown for a signature never reported.
  if (
    !httpErrorThrottle.shouldReport(
      eventSignature(service, request, error, diagnostics)
    )
  ) {
    return;
  }

  Sentry.withScope((scope) => {
    // Tags are filterable/searchable in the Sentry UI.
    scope.setTag('service', service);
    scope.setTag('status_code', statusCode);
    scope.setTag('http.method', request.method);

    if (diagnostics) {
      scope.setTag('network_failure', diagnostics.reason);
      scope.setTag('network_timing', diagnostics.speed);
      if (diagnostics.blockedStatus) {
        scope.setTag('blocked_status', String(diagnostics.blockedStatus));
      }
    }

    // Read back off the request rather than from the scope: a `status === 0` is
    // reported a couple of seconds late, and a navigation inside that window
    // gives the scope a new propagation context — the ids on the issue would
    // then name a trace the backend never saw. What went out on the wire is on
    // the request, and cannot drift.
    const traceparent = request.headers.get('traceparent');
    const trace = traceparent ? TRACEPARENT_PATTERN.exec(traceparent) : null;
    if (trace) {
      scope.setTag('trace_id', trace[1]);
      scope.setTag('span_id', trace[2]);
    }

    // Anonymous requests (login, password reset, or a session whose token is
    // already gone) have no identity to report, so the user stays unset.
    if (user) {
      scope.setUser({
        id: user.id,
        personId: user.personId,
        tenantId: user.tenantId,
      });
      scope.setTag('person_id', user.personId);
      scope.setTag('tenant_id', user.tenantId);
    }

    // Group into one issue per service + method + endpoint + status + message,
    // instead of letting Sentry group by the (identical) synthetic stack trace,
    // which would merge every endpoint into a single issue. The backend message
    // is part of the fingerprint so distinct failures on the same endpoint+status
    // (e.g. different validation errors) stay in separate issues, matching the
    // title. When there is no message, the entry is omitted so those errors still
    // group by endpoint+status.
    scope.setFingerprint([
      'sentry-http-error',
      service,
      request.method,
      fingerprintPath(request.url),
      statusCode,
      // Without this every `status === 0` on an endpoint lands in one issue, so
      // a user in a lift and a gateway answering without CORS headers share a
      // bucket and neither is diagnosable.
      ...(diagnostics ? [diagnostics.reason, diagnostics.speed] : []),
      ...(errorMessage ? [errorMessage] : []),
    ]);

    scope.setContext('request', {
      method: request.method,
      url: redactUrl(request.urlWithParams),
      status: error.status,
      statusText: error.statusText,
      service,
      // Client-side duration. On a failure with no response it is the only
      // measure of what happened, and it separates "died on the spot" (blocked,
      // refused) from "hung until something upstream gave up" — the ~30s/~60s
      // marks of a gateway limit.
      elapsedMs,
      // The raw header, so it can be pasted straight into a backend log search.
      traceparent,
    });

    const responseHeaders = readableResponseHeaders(error);
    if (responseHeaders) {
      scope.setContext('response_headers', responseHeaders);
    }

    if (diagnostics) {
      scope.setContext('network', {
        reason: diagnostics.reason,
        blockedStatus: diagnostics.blockedStatus,
        timingDurationMs: diagnostics.timingDurationMs,
        online: diagnostics.online,
        visibility: diagnostics.visibility,
        ...(diagnostics.connection ?? {}),
      });
    }

    // The curl and response body live in "Additional Data" (extra) so they are
    // readable inline on the Sentry issue without downloading an attachment.
    scope.setExtra('curl', curl);

    const response = serializeResponse(error.error);
    if (response != null) {
      scope.setExtra('response', response);
    }

    // Append the parsed backend message (when available) so the issue title is
    // descriptive instead of just status + endpoint.
    // The classification goes in the title so the issue list distinguishes the
    // `status === 0` buckets at a glance, matching the fingerprint split above.
    const statusLabel = diagnostics
      ? `HTTP 0 (${diagnostics.reason}, ${diagnostics.speed})`
      : `HTTP ${error.status}`;
    const title = `[${service}] ${statusLabel} on ${
      request.method
    } ${fingerprintUrl(request.url)}`;

    Sentry.captureException(
      error.error instanceof Error
        ? error.error
        : new Error(errorMessage ? `${title} — ${errorMessage}` : title)
    );
  });
}

/**
 * HTTP interceptor that reports failed requests to Sentry — **only in the `prod`
 * environment**, and only to hosts the environment itself declares in
 * `APP_CONFIG.apiUrl` (see {@link monitoredHosts}). For every error response it
 * captures:
 *  - a reproducible `curl` of the original request (sensitive headers omitted,
 *    credentials and card numbers redacted — see {@link isSensitiveKey} and
 *    {@link looksLikeCardNumber}), exposed under "Additional Data";
 *  - the HTTP response body (object or plain text), also under "Additional
 *    Data", and the response headers the browser lets us read;
 *  - `service` and `status_code` tags so issues can be filtered in Sentry, the
 *    `service` tag named by {@link resolveServiceTag};
 *  - `trace_id` and `span_id` tags, read back off the request this interceptor
 *    stamped on the way out (see {@link traceHeaders}), which is what lets a
 *    Sentry issue be matched against a backend log line. They travel outbound
 *    because the failure this file most needs to explain — `status === 0` —
 *    delivers no response to JavaScript at all, so nothing coming back can
 *    carry an identifier.
 *
 * It used to report a hand-written list of six services, which meant a backend
 * was invisible until someone remembered to register it. What is *not* reported
 * is now decided per failure, by what the failure means:
 *  - a host that is not one of ours — a third party, or the app's own static
 *    files;
 *  - failures caused by the document unloading (reload, tab close, navigation
 *    away) — see {@link shouldReport};
 *  - a read that died with no network, which `OfflineRetryInterceptor` replays —
 *    see {@link READ_METHODS};
 *  - the first 401, the expected token expiry the `TokenInterceptor` refreshes;
 *  - a repeat of a failure already reported inside the cooldown, or anything
 *    past the session cap — see {@link httpErrorThrottle}.
 *
 * Requests that are not reported pass through untouched, and the original error
 * is always re-thrown so existing store error handlers keep working.
 */
export const SentryHttpErrorInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): ReturnType<HttpHandlerFn> => {
  // Only report to Sentry in production; in other environments this is a no-op.
  const config = inject(APP_CONFIG);
  const service =
    config.environment === 'prod'
      ? resolveServiceTag(request.url, monitoredHosts(config.apiUrl))
      : null;

  if (!service) {
    return next(request);
  }

  trackPageUnload();
  trackResourceTimings();

  const errorStore = inject(ErrorStore);
  const authStore = inject(AuthStore, { optional: true });
  const localStorageFacade = inject(LocalStorageFacade);

  /**
   * Best-effort read of the current auth data. The store is the source of truth,
   * but it starts empty and is only filled by the login flow — after a page reload
   * the token lives solely in localStorage, so a store miss falls back to storage
   * (`actuar/`-prefixed key first, legacy un-prefixed one after). Called on failure
   * rather than up-front, so the identity is the one in effect when the request
   * failed. Mirrors `readAuth` in the token interceptor.
   */
  const readAuth = (): IAuthAccessToken | null => {
    const storeAuth = authStore?.getAuthData();
    if (storeAuth) {
      return storeAuth;
    }

    try {
      const raw =
        localStorageFacade.getItem(ELocalStorageKeys.auth) ??
        localStorageFacade.getItemRaw(ELocalStorageKeys.auth);
      return raw ? (JSON.parse(raw) as IAuthAccessToken) : null;
    } catch {
      return null;
    }
  };

  // Marked before handing the request downstream (this interceptor is last in
  // the chain, so what is measured is the request itself, not the token refresh
  // or anything else upstream). `performance.now()` and not a date: this is a
  // duration, and it must not move when the system clock does.
  // Attached here rather than in an interceptor of its own: this one is last in
  // the chain, so the headers ride the request that actually leaves — the token
  // refresh has already replayed by now — and it is already the file that knows
  // which hosts are ours. Requests to anyone else returned above and are never
  // given our trace ids.
  const outboundTrace = traceHeaders();
  const tracedRequest = Object.keys(outboundTrace).length
    ? request.clone({ setHeaders: outboundTrace })
    : request;

  const startedAt = performance.now();

  return next(tracedRequest).pipe(
    catchError((error: unknown) => {
      if (
        error instanceof HttpErrorResponse &&
        shouldReport(error, tracedRequest)
      ) {
        const elapsedMs = Math.round(performance.now() - startedAt);

        let errorMessage: string | null = null;
        try {
          errorMessage = errorStore.getHttpErrorMessage(error);
        } catch {
          // Prevent parsing errors from blocking Sentry reporting
        }

        let user: SentryUser | null = null;
        try {
          user = resolveUser(readAuth());
        } catch {
          // Never let an identity lookup block the report itself
        }

        const report = () =>
          reportToSentry(
            tracedRequest,
            error,
            service,
            errorMessage,
            user,
            startedAt,
            elapsedMs
          );

        // A `status === 0` is classified on a delay because both inputs arrive
        // late: the Resource Timing entry is queued asynchronously and can land
        // after the XHR error event, and `navigator.onLine` lags the failure it
        // explains by tens of milliseconds. Reading either on the spot misses
        // the entry and mistakes an outage for something else — which, now that
        // an offline read is dropped rather than reported, would put the noise
        // straight back. The window is the one `OfflineRetryInterceptor` waits
        // before deciding, so both interceptors classify the same failure the
        // same way.
        if (error.status === 0) {
          // Re-checked because the unload may have started during the wait, and
          // an unloading page's `status === 0` says nothing about the backend.
          setTimeout(() => {
            if (shouldReport(error, tracedRequest)) report();
          }, OFFLINE_CONFIRM_MS);
        } else {
          report();
        }
      }
      return throwError(() => error);
    })
  );
};
